import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useAuth } from "../context/AuthContext";
import {
  doc,
  setDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useParams } from "react-router-dom";
import {
  CheckCircle,
  XCircle,
  Sparkles,
  ShieldCheck,
  Mail,
  User as UserIcon,
  LogOut,
  Trophy,
  Target,
  CalendarDays,
  Crown,
  Gem,
  Medal,
} from "lucide-react";

/* ================= STYLES ================= */

const Page = styled.div`
  min-height: 100vh;
  padding: 120px 20px;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Card = styled.div`
  max-width: 980px;
  margin: auto;
  border-radius: 30px;
  padding: 32px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
`;

const Hero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(280px, 0.9fr);
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Header = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const Avatar = styled.img`
  width: 84px;
  height: 84px;
  border-radius: 24px;
  object-fit: cover;
  border: 1px solid rgba(148, 163, 184, 0.18);
`;

const AvatarFallback = styled.div`
  width: 84px;
  height: 84px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.24), rgba(56, 189, 248, 0.18));
  color: #f8fafc;
  font-size: 26px;
  font-weight: 1000;
  text-transform: uppercase;
`;

const Name = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 900;
  color: #f8fafc;
`;

const Email = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  color: #94a3b8;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 16px;
`;

const HeroSub = styled.p`
  margin: 14px 0 0;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.7;
  max-width: 620px;
`;

const SummaryPanel = styled.div`
  padding: 24px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(56, 189, 248, 0.08)),
    rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(245, 158, 11, 0.18);
`;

const SummaryLabel = styled.div`
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SummaryTitle = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 1000;
`;

const SummaryList = styled.div`
  margin-top: 18px;
  display: grid;
  gap: 10px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 16px;
  background: rgba(2, 6, 23, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 700;
`;

const ContentGrid = styled.div`
  margin-top: 28px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 0.8fr);
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StatsStrip = styled.div`
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.62);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const StatLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 800;
`;

const StatValue = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 28px;
  font-weight: 1000;
  line-height: 1;
`;

const StatHint = styled.div`
  margin-top: 8px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
`;

const LevelBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $tone }) =>
    $tone === "platinum"
      ? "#67e8f9"
      : $tone === "gold"
        ? "#fde68a"
        : $tone === "silver"
          ? "#e2e8f0"
          : "#fdba74"};
  background: ${({ $tone }) =>
    $tone === "platinum"
      ? "linear-gradient(135deg, rgba(56, 189, 248, 0.18), rgba(103, 232, 249, 0.12))"
      : $tone === "gold"
        ? "rgba(245, 158, 11, 0.14)"
        : $tone === "silver"
          ? "rgba(148, 163, 184, 0.14)"
          : "rgba(249, 115, 22, 0.14)"};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "platinum"
        ? "rgba(56, 189, 248, 0.34)"
        : $tone === "gold"
          ? "rgba(245, 158, 11, 0.3)"
          : $tone === "silver"
            ? "rgba(148, 163, 184, 0.3)"
            : "rgba(249, 115, 22, 0.28)"};
`;

const ProgressTrack = styled.div`
  margin-top: 14px;
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.88);
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.1);
`;

const ProgressFill = styled.div`
  width: ${({ $percent }) => `${$percent}%`};
  height: 100%;
  background: linear-gradient(90deg, #f59e0b, #38bdf8);
`;

const ActivityGrid = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(10, minmax(0, 1fr));
  gap: 6px;
`;

const ActivityCell = styled.div`
  aspect-ratio: 1 / 1;
  border-radius: 6px;
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(245, 158, 11, 0.9), rgba(56, 189, 248, 0.92))"
      : "rgba(15, 23, 42, 0.68)"};
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(245, 158, 11, 0.24)" : "rgba(148, 163, 184, 0.08)"};
`;

const Section = styled.div`
  padding: 24px;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.62);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
`;

const Label = styled.span`
  font-weight: 700;
  font-size: 14px;
  color: #cbd5e1;
`;

const Value = styled.span`
  font-size: 14px;
  color: #f8fafc;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  margin-top: 10px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  padding: 0 14px;
  background: rgba(2, 6, 23, 0.52);
  color: #f8fafc;
`;

const Button = styled.button`
  height: 48px;
  margin-top: 16px;
  border-radius: 14px;
  border: none;
  font-weight: 900;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const Primary = styled(Button)`
  width: 100%;
  background: linear-gradient(90deg, #f59e0b, #d97706);
  color: white;
`;

const Danger = styled(Button)`
  width: 100%;
  background: #fee2e2;
  color: #b91c1c;
`;

/* ================= CONFIRM MODAL ================= */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 420px;
  background: ${({ theme }) => theme.colors.cardBg};
  border-radius: 20px;
  padding: 28px;
  box-shadow: ${({ theme }) => theme.shadows.card};
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 900;
`;

const ModalText = styled.p`
  margin-top: 10px;
  font-size: 14px;
  opacity: 0.8;
  line-height: 1.6;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Secondary = styled(Button)`
  flex: 1;
  background: ${({ theme }) => theme.colors.bgElevated};
  color: ${({ theme }) => theme.colors.text};
`;

const SectionTitle = styled.div`
  color: #f8fafc;
  font-size: 18px;
  font-weight: 900;
`;

const SectionSub = styled.p`
  margin: 8px 0 0;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.7;
`;

/* ================= COMPONENT ================= */

export default function ProfilePage() {
  const { uid: profileUid } = useParams();
  const { user, logout, resetPassword } = useAuth();
  const functions = getFunctions();
  const cancelSubscription = httpsCallable(functions, "cancelSubscription");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [name, setName] = useState("");
  const [plan, setPlan] = useState("FREE");
  const [subStatus, setSubStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [hasRoadmap, setHasRoadmap] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [readonlyProfile, setReadonlyProfile] = useState(null);
  const [coins, setCoins] = useState(0);
  const [activePlanTitle, setActivePlanTitle] = useState("");
  const [activePlanType, setActivePlanType] = useState("");
  const [roadmapStats, setRoadmapStats] = useState({
    targetRole: "",
    completedDays: 0,
    completedMap: {},
  });
  const isReadonlyProfile = !!profileUid && profileUid !== user?.uid;
  const activeUid = isReadonlyProfile ? profileUid : user?.uid;

  /* ---------- Load user + subscription ---------- */
  useEffect(() => {
    if (!user || !activeUid) return;
    setAvatarError(false);
    setProfileLoading(true);

    const unsubUser = onSnapshot(doc(db, "users", activeUid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setName(data.name || "");
        setPlan(data.plan || "FREE");
        setCoins(Number(data.coins || 0));
        setActivePlanTitle(String(data.activePlanTitle || ""));
        setActivePlanType(String(data.activePlanType || ""));
        setReadonlyProfile({
          uid: activeUid,
          email: isReadonlyProfile ? "" : data.email || user.email || "",
          photo: data.photo || data.photoURL || "",
        });
      } else if (isReadonlyProfile) {
        setName("");
        setPlan("FREE");
        setCoins(0);
        setActivePlanTitle("");
        setActivePlanType("");
        setReadonlyProfile({
          uid: activeUid,
          email: "",
          photo: "",
        });
      }
      setProfileLoading(false);
    });

    const unsubRoadmap = onSnapshot(doc(db, "roadmaps", activeUid), (snap) => {
      setHasRoadmap(snap.exists());
      if (snap.exists()) {
        const roadmapData = snap.data() || {};
        const completedMap = roadmapData.completedDays || {};
        setRoadmapStats({
          targetRole:
            roadmapData.direction?.role ||
            roadmapData.direction?.title ||
            "Roadmap in progress",
          completedDays: Object.keys(completedMap).length,
          completedMap,
        });
      } else {
        setRoadmapStats({
          targetRole: "",
          completedDays: 0,
          completedMap: {},
        });
      }
    });

    let unsubSub = () => {};
    if (!isReadonlyProfile) {
      unsubSub = onSnapshot(doc(db, "subscriptions", activeUid), (snap) => {
        if (snap.exists()) {
          setSubStatus(snap.data().status);
        } else {
          setSubStatus(null);
        }
      });
    } else {
      setSubStatus(null);
    }

    return () => {
      unsubUser();
      unsubSub();
      unsubRoadmap();
    };
  }, [activeUid, isReadonlyProfile, user]);

  useEffect(() => {
    if (!user || isReadonlyProfile) return;
    setReadonlyProfile({
      uid: user.uid,
      email: user.email || "",
      photo: user.photo || user.photoURL || "",
    });
  }, [isReadonlyProfile, user]);

  /* ---------- Save name ---------- */
  const saveProfile = async () => {
    try {
      setSaving(true);
      await setDoc(
        doc(db, "users", user.uid),
        { name, updatedAt: Date.now() },
        { merge: true }
      );
      alert("Profile updated ✅");
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Cancel subscription ---------- */
  const handleCancelConfirm = async () => {
    try {
      setCancelling(true);
      await cancelSubscription();
      setShowCancelModal(false);
      alert("Subscription cancelled successfully");
    } catch (err) {
      alert("Failed to cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  const handlePasswordReset = async () => {
    try {
      setSendingReset(true);
      await resetPassword(user.email);
      alert("Password reset link sent to your email.");
    } catch (err) {
      alert(err?.message || "Could not send reset link.");
    } finally {
      setSendingReset(false);
    }
  };

  if (!user) return null;

  const getLearnerLevel = (coinCount) => {
    if (coinCount >= 10000) {
      return {
        name: "Platinum Learner",
        tone: "platinum",
        icon: Gem,
        nextMilestone: 10000,
      };
    }

    if (coinCount >= 5000) {
      return {
        name: "Gold Learner",
        tone: "gold",
        icon: Crown,
        nextMilestone: 10000,
      };
    }

    if (coinCount >= 1000) {
      return {
        name: "Silver Learner",
        tone: "silver",
        icon: Medal,
        nextMilestone: 5000,
      };
    }

    return {
      name: "Bronze Learner",
      tone: "bronze",
      icon: Trophy,
      nextMilestone: 1000,
    };
  };

  const levelMeta = getLearnerLevel(coins);
  const LevelIcon = levelMeta.icon;
  const progressToNext = Math.max(
    0,
    Math.min(100, (coins / levelMeta.nextMilestone) * 100)
  );
  const activityCells = Array.from({ length: 70 }, (_, index) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - (69 - index));
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate()
    ).padStart(2, "0")}`;
    return {
      key,
      active: !!roadmapStats.completedMap?.[key],
    };
  });

  const profileEmail = isReadonlyProfile
    ? ""
    : readonlyProfile?.email || user.email || "";
  const displayPlanTitle =
    activePlanTitle || (plan === "PRO" ? "PRO" : "FREE");
  const avatarSrc = readonlyProfile?.photo || (!isReadonlyProfile ? user.photo || user.photoURL || "" : "");

  const initials = (name || profileEmail || "U")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("");

  const showSubscriptionSection = !isReadonlyProfile && (plan === "PRO" || !!subStatus || hasRoadmap);
  const summaryTitle = isReadonlyProfile
    ? hasRoadmap
      ? "Roadmap member profile"
      : "Community member profile"
    : plan === "PRO"
      ? "PRO access active"
      : "Free plan active";
  const summaryItems = isReadonlyProfile
    ? [
        hasRoadmap
          ? "This developer currently has an active roadmap on skilll.dev"
          : "This developer is part of the skilll.dev community",
        "Email is not shared publicly",
        name || "Community profile",
      ]
    : [
        plan === "PRO"
          ? "You currently have premium roadmap systems enabled"
          : "You are using the free roadmap experience",
        `${coins} coins • ${levelMeta.name}`,
        roadmapStats.targetRole || name || "Add your name to personalize your profile",
      ];

  return (
    <Page>
      <Card>
        <Hero>
          <div>
            <Eyebrow>
              <Sparkles size={14} />
              {isReadonlyProfile ? "Community Profile" : "Account Profile"}
            </Eyebrow>

            <Header>
              {avatarSrc && !avatarError ? (
                <Avatar
                  src={avatarSrc}
                  alt={name || "Profile avatar"}
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <AvatarFallback>{initials}</AvatarFallback>
              )}
              <div>
                <Name>
                  {profileLoading
                    ? "Loading profile..."
                    : name || (isReadonlyProfile ? "Community Profile" : "Your Profile")}
                </Name>
                {profileEmail ? <Email>{profileEmail}</Email> : null}
              </div>
            </Header>

            <HeroSub>
              {isReadonlyProfile
                ? "View this developer's public profile in a clean read-only mode."
                : "Manage your profile details, view your current plan, and control your subscription from one place."}
            </HeroSub>
          </div>

          <SummaryPanel>
            <SummaryLabel>{isReadonlyProfile ? "Profile Summary" : "Account Summary"}</SummaryLabel>
            <SummaryTitle>{summaryTitle}</SummaryTitle>
            <SummaryList>
              <SummaryItem>
                <ShieldCheck size={16} />
                {summaryItems[0]}
              </SummaryItem>
              <SummaryItem>
                <Mail size={16} />
                {summaryItems[1]}
              </SummaryItem>
              <SummaryItem>
                <UserIcon size={16} />
                {summaryItems[2]}
              </SummaryItem>
            </SummaryList>
          </SummaryPanel>
        </Hero>

        <StatsStrip>
          <StatCard>
            <StatLabel>
              <Trophy size={14} />
              Coin balance
            </StatLabel>
            <StatValue>{coins}</StatValue>
            <StatHint>Earn 100 coins for each roadmap day you complete.</StatHint>
          </StatCard>

          <StatCard>
            <StatLabel>
              <LevelIcon size={14} />
              Learner level
            </StatLabel>
            <StatValue style={{ fontSize: 20 }}>{levelMeta.name}</StatValue>
            <StatHint>Silver at 1000 coins. Gold at 5000 coins. Platinum at 10000 coins.</StatHint>
          </StatCard>

          <StatCard>
            <StatLabel>
              <Target size={14} />
              Target roadmap
            </StatLabel>
            <StatValue style={{ fontSize: 18 }}>
              {roadmapStats.targetRole || "Not started"}
            </StatValue>
            <StatHint>Your current direction inside skilll.dev.</StatHint>
          </StatCard>

          <StatCard>
            <StatLabel>
              <CalendarDays size={14} />
              Days completed
            </StatLabel>
            <StatValue>{roadmapStats.completedDays}</StatValue>
            <StatHint>Visible execution proof from your roadmap journey.</StatHint>
          </StatCard>
        </StatsStrip>

        <Section style={{ marginTop: 20 }}>
          <SectionTitle>Consistency activity</SectionTitle>
          <SectionSub>
            A simple Git-style view of recently completed roadmap days.
          </SectionSub>
          <ActivityGrid>
            {activityCells.map((cell) => (
              <ActivityCell
                key={cell.key}
                $active={cell.active}
                title={cell.active ? `${cell.key}: completed` : `${cell.key}: no completion`}
              />
            ))}
          </ActivityGrid>
        </Section>

        <ContentGrid>
          <Section>
            <SectionTitle>{isReadonlyProfile ? "Profile Details" : "Profile Details"}</SectionTitle>
            <SectionSub>
              {isReadonlyProfile
                ? "Basic public information is shown here so community interactions feel more personal."
                : "Keep your account details updated so your roadmap and community profile feel personal."}
            </SectionSub>

            <Section style={{ marginTop: 18, padding: 0, background: "transparent", border: "none" }}>
              <Label>Name</Label>
              {isReadonlyProfile ? (
                <Input value={name || "Not shared"} readOnly />
              ) : (
                <>
                  <Input value={name} onChange={(e) => setName(e.target.value)} />
                  <Primary onClick={saveProfile} disabled={saving}>
                    {saving ? "Saving..." : "Save Profile"}
                  </Primary>
                </>
              )}
            </Section>
          </Section>

          {isReadonlyProfile ? (
            <Section>
              <SectionTitle>Roadmap Status</SectionTitle>
              <SectionSub>
                A quick read-only snapshot of this member's product activity.
              </SectionSub>

              <div style={{ marginTop: 18 }}>
                <Row>
                  <Label>Roadmap</Label>
                  <Value>{hasRoadmap ? "Active" : "Not active yet"}</Value>
                </Row>
                <Row>
                  <Label>Plan</Label>
                  <Value>{plan === "PRO" ? "PRO" : "FREE"}</Value>
                </Row>
                <Row>
                  <Label>Coins</Label>
                  <Value>{coins}</Value>
                </Row>
                <Row>
                  <Label>Completed Days</Label>
                  <Value>{roadmapStats.completedDays}</Value>
                </Row>
                <Row>
                  <Label>Roadmap Target</Label>
                  <Value>{roadmapStats.targetRole || "Not started yet"}</Value>
                </Row>
              </div>
            </Section>
          ) : (
            <Section>
              <SectionTitle>Account Actions</SectionTitle>
              <SectionSub>
                Password and account access controls live here.
              </SectionSub>

              <Primary
                style={{ marginTop: 18 }}
                onClick={handlePasswordReset}
                disabled={sendingReset}
              >
                <ShieldCheck size={16} />
                {sendingReset ? "Sending reset link..." : "Change Password"}
              </Primary>

              <Primary
                style={{ marginTop: 12, background: "rgba(148,163,184,0.16)" }}
                onClick={logout}
              >
                <LogOut size={16} />
                Logout
              </Primary>
            </Section>
          )}
        </ContentGrid>

        {showSubscriptionSection && (
          <Section style={{ marginTop: 20 }}>
            <SectionTitle>Subscription</SectionTitle>
            <SectionSub>
              Review your plan and manage billing-related actions here.
            </SectionSub>

            <div style={{ marginTop: 18 }}>
              <Row>
                <Label>Plan</Label>
                <Value>
                  {plan === "PRO" ? (
                    <>
                      <CheckCircle size={14} /> {displayPlanTitle}
                    </>
                  ) : (
                    "FREE"
                  )}
                </Value>
              </Row>

              {activePlanType ? (
                <Row>
                  <Label>Plan Type</Label>
                  <Value>{String(activePlanType).replace(/-/g, " ")}</Value>
                </Row>
              ) : null}

              {subStatus && (
                <Row>
                  <Label>Status</Label>
                  <Value>{subStatus}</Value>
                </Row>
              )}
            </div>

            {plan === "PRO" && (
              <>
                <Danger style={{ marginTop: 18 }} onClick={() => setShowCancelModal(true)}>
                  <XCircle size={16} /> Cancel Subscription
                </Danger>
              </>
            )}
          </Section>
        )}

        <Section style={{ marginTop: 20 }}>
          <SectionTitle>Coins and learning level</SectionTitle>
          <SectionSub>
            Complete one roadmap day to unlock 100 coins. Your level rises with visible consistency, not random activity.
          </SectionSub>

          <div style={{ marginTop: 18 }}>
            <LevelBadge $tone={levelMeta.tone}>
              <LevelIcon size={14} />
              {levelMeta.name}
            </LevelBadge>
            <ProgressTrack>
              <ProgressFill $percent={progressToNext} />
            </ProgressTrack>
            <SectionSub style={{ marginTop: 10 }}>
              {coins >= levelMeta.nextMilestone
                ? "You have crossed this level milestone."
                : `${Math.max(levelMeta.nextMilestone - coins, 0)} more coins to reach the next milestone.`}
            </SectionSub>
          </div>
        </Section>
      </Card>
      {showCancelModal && (
        <Overlay>
          <Modal>
            <ModalTitle>Cancel Subscription?</ModalTitle>
            <ModalText>
              Your PRO access will remain active until the end of the current
              billing cycle. After that, your plan will downgrade to FREE.
            </ModalText>

            <ModalActions>
              <Secondary onClick={() => setShowCancelModal(false)}>
                Keep Subscription
              </Secondary>

              <Danger onClick={handleCancelConfirm} disabled={cancelling}>
                {cancelling ? "Cancelling..." : "Yes, Cancel"}
              </Danger>
            </ModalActions>
          </Modal>
        </Overlay>
      )}
    </Page>
  );
}
